/**
 * A1 打印订单列表页
 *
 * Stream 2 Phase 2-1b / Plan §10 Stream 2 / Design §4.2 A 履约.
 *
 * 功能：
 *  - 筛选区（status / userId / workId / paymentStatus / logisticsStatus / 日期范围）
 *  - antd Table 展示订单列表（分页）
 *  - orderId 列点击跳转详情
 *  - 整页用 PermissionGate ORDER_READ 包裹，无权显示 403
 *
 * 权限：order:read（operator / support / finance / super_admin 均持有）。
 * 注意：UI 隐藏不是安全边界 —— 真正校验在 admin-service requirePermission。
 */
import { useState } from 'react';
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Result,
  Card,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  listOrders,
  type OrderListItem,
  type OrderStatus,
  type ListOrdersFilter,
} from '../../../api/orders';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ORDER_STATUS_OPTIONS: { value: OrderStatus | ''; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'pending_payment', label: '待支付' },
  { value: 'paid', label: '已支付' },
  { value: 'shipped', label: '已发货' },
  { value: 'completed', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'pending', label: 'pending' },
  { value: 'paid', label: 'paid' },
  { value: 'refunded', label: 'refunded' },
  { value: 'failed', label: 'failed' },
];

const LOGISTICS_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'pending', label: 'pending' },
  { value: 'shipped', label: 'shipped' },
  { value: 'delivered', label: 'delivered' },
];

function orderStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'pending_payment': return 'default';
    case 'paid':            return 'blue';
    case 'shipped':         return 'gold';
    case 'completed':       return 'green';
    case 'cancelled':       return 'red';
    default:                return 'default';
  }
}

function orderStatusLabel(status: OrderStatus): string {
  switch (status) {
    case 'pending_payment': return '待支付';
    case 'paid':            return '已支付';
    case 'shipped':         return '已发货';
    case 'completed':       return '已完成';
    case 'cancelled':       return '已取消';
    default:                return status;
  }
}

/** 分转元，保留 2 位小数 */
function centToYuan(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/** ISO 日期 → YYYY-MM-DD HH:mm */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

interface FilterFormValues {
  status?: OrderStatus | '';
  userId?: string;
  workId?: string;
  paymentStatus?: string;
  logisticsStatus?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

function OrdersListPageInner() {
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [filter, setFilter] = useState<ListOrdersFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['orders', filter, page, pageSize],
    queryFn: () => listOrders({ ...filter, page, pageSize }),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListOrdersFilter = {};
    if (v.status) next.status = v.status as OrderStatus;
    if (v.userId?.trim()) next.userId = v.userId.trim();
    if (v.workId?.trim()) next.workId = v.workId.trim();
    if (v.paymentStatus) next.paymentStatus = v.paymentStatus;
    if (v.logisticsStatus) next.logisticsStatus = v.logisticsStatus;
    if (v.dateRange?.[0]) next.fromDate = v.dateRange[0].toISOString();
    if (v.dateRange?.[1]) next.toDate = v.dateRange[1].toISOString();
    setFilter(next);
    setPage(1);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilter({});
    setPage(1);
  }

  const columns: ColumnsType<OrderListItem> = [
    {
      title: '订单号',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 200,
      render: (id: string) => (
        <Link to={`/orders/${encodeURIComponent(id)}`} data-testid={`order-link-${id}`}>
          {id}
        </Link>
      ),
    },
    {
      title: '用户 ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      ellipsis: true,
      render: (id: string) => (
        <Tooltip title={id}>
          <Text ellipsis style={{ maxWidth: 160 }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '作品 ID',
      dataIndex: 'workId',
      key: 'workId',
      width: 160,
      ellipsis: true,
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: OrderStatus) => (
        <Tag color={orderStatusColor(s)}>{orderStatusLabel(s)}</Tag>
      ),
    },
    {
      title: '支付状态',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 100,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '物流状态',
      dataIndex: 'logisticsStatus',
      key: 'logisticsStatus',
      width: 100,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 90,
      render: (v: number) => centToYuan(v),
    },
    {
      title: '收件人',
      dataIndex: 'receiverName',
      key: 'receiverName',
      width: 100,
    },
    {
      title: '手机（脱敏）',
      dataIndex: 'receiverPhoneMasked',
      key: 'receiverPhoneMasked',
      width: 130,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: unknown, row: OrderListItem) => (
        <Link to={`/orders/${encodeURIComponent(row.orderId)}`}>详情</Link>
      ),
    },
  ];

  return (
    <Card
      title={
        <span>
          打印订单
          <Text type="secondary" style={{ fontSize: 13, marginLeft: 12, fontWeight: 400 }}>
            履约 / 客服处理订单
          </Text>
        </span>
      }
      data-testid="orders-list-page"
    >
      <Form
        form={filterForm}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
        data-testid="orders-filter-form"
      >
        <Form.Item name="status" label="订单状态">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 120 }}
            options={ORDER_STATUS_OPTIONS.filter((o) => o.value !== '')}
          />
        </Form.Item>
        <Form.Item name="userId" label="用户 ID">
          <Input placeholder="userId" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="workId" label="作品 ID">
          <Input placeholder="workId" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="paymentStatus" label="支付状态">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 120 }}
            options={PAYMENT_STATUS_OPTIONS.filter((o) => o.value !== '')}
          />
        </Form.Item>
        <Form.Item name="logisticsStatus" label="物流状态">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 120 }}
            options={LOGISTICS_STATUS_OPTIONS.filter((o) => o.value !== '')}
          />
        </Form.Item>
        <Form.Item name="dateRange" label="创建时间">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              onClick={applyFilters}
              data-testid="orders-search"
            >
              查询
            </Button>
            <Button onClick={resetFilters} data-testid="orders-reset">
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<OrderListItem>
        rowKey="orderId"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        scroll={{ x: 1300 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        data-testid="orders-table"
      />
    </Card>
  );
}

export default function OrdersListPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.ORDER_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有查看订单列表的权限，请联系管理员申请 order:read 权限。"
        />
      }
    >
      <OrdersListPageInner />
    </PermissionGate>
  );
}
